/**
 * PAGE: ADMIN — Modèles d'email
 * Item #7 retours client : page de gestion des templates email réutilisables.
 * Lecture pour tous les authentifiés (utile au dropdown dans SendBulkEmailModal),
 * écriture réservée aux admin/superadmin (RLS).
 */

import { useState } from 'react';
import { Mail, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageHeader, PageLayout } from '../components/page';
import { EmailTemplateModal } from '../components/modals';
import { ConfirmDialog, ErrorState, LoadingState, toast } from '../components/ui';
import { useEmailTemplates } from '../hooks/useEmailTemplates';
import { usePermissions } from '../hooks/usePermissions';
import type { EmailTemplate } from '../types/emailTemplate';

export function EmailTemplatesPage() {
  const { templates, loading, error, create, update, remove } = useEmailTemplates();
  const { canManageAssignments } = usePermissions();
  const canEdit = canManageAssignments(); // admin/superadmin

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    template: EmailTemplate | null;
  }>({ open: false, template: null });

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleSubmit = async (data: {
    name: string;
    subject: string;
    body: string;
    entity_scope: 'all' | 'crew' | 'contact';
  }) => {
    try {
      if (editingTemplate) {
        const result = await update(editingTemplate.id, data);
        if (result) {
          toast.success('Modèle mis à jour');
        } else {
          toast.error('Erreur lors de la mise à jour');
        }
      } else {
        const result = await create(data);
        if (result) {
          toast.success('Modèle créé');
        } else {
          toast.error('Erreur lors de la création');
        }
      }
    } catch (err) {
      console.error('Erreur EmailTemplatesPage:', err);
      toast.error('Erreur', {
        description: err instanceof Error ? err.message : 'Opération impossible',
      });
    }
  };

  const handleDeleteClick = (template: EmailTemplate) => {
    setDeleteConfirm({ open: true, template });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.template) return;
    const success = await remove(deleteConfirm.template.id);
    if (success) {
      toast.success('Modèle supprimé');
    } else {
      toast.error('Erreur lors de la suppression');
    }
    setDeleteConfirm({ open: false, template: null });
  };

  if (loading) {
    return (
      <PageLayout
        header={<PageHeader breadcrumbs={[{ label: 'Admin' }, { label: "Modèles d'email" }]} />}
      >
        <LoadingState message="Chargement des modèles..." />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        header={<PageHeader breadcrumbs={[{ label: 'Admin' }, { label: "Modèles d'email" }]} />}
      >
        <ErrorState
          title="Erreur lors du chargement"
          message={error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={<PageHeader breadcrumbs={[{ label: 'Admin' }, { label: "Modèles d'email" }]} />}
    >
      <div className="w-full h-px bg-zinc-200" />

      <div className="flex flex-col gap-6 p-6">
        {/* En-tête + bouton créer */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">Modèles d'email</h1>
            <p className="mt-1 text-sm text-zinc-600">
              Bibliothèque de modèles réutilisables dans les envois en masse.
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={handleCreate}
              className="inline-flex items-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800 transition-colors"
            >
              <Plus className="size-4" />
              Nouveau modèle
            </button>
          )}
        </div>

        {/* Liste */}
        {templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-zinc-300 bg-white py-16">
            <Mail className="size-10 text-zinc-300" />
            <p className="text-sm text-zinc-600">Aucun modèle pour l'instant.</p>
            {canEdit && (
              <button
                type="button"
                onClick={handleCreate}
                className="text-sm font-medium text-zinc-900 underline hover:no-underline"
              >
                Créer le premier modèle
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
            <table className="w-full">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Nom
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Sujet
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Visible depuis
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
                    Mis à jour
                  </th>
                  {canEdit && (
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-600">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr
                    key={template.id}
                    className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">{template.name}</td>
                    <td className="px-4 py-3 text-sm text-zinc-700">
                      <div className="max-w-md truncate">{template.subject}</div>
                    </td>
                    <td className="px-4 py-3">
                      {template.entity_scope === 'crew' ? (
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                          Équipage
                        </span>
                      ) : template.entity_scope === 'contact' ? (
                        <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-inset ring-violet-200">
                          Contact
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500 ring-1 ring-inset ring-zinc-200">
                          Toutes
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-500">
                      {new Date(template.updated_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(template)}
                            className="inline-flex items-center justify-center size-8 rounded-md text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
                            aria-label="Modifier"
                            title="Modifier"
                          >
                            <Pencil className="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(template)}
                            className="inline-flex items-center justify-center size-8 rounded-md text-zinc-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                            aria-label="Supprimer"
                            title="Supprimer"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modale création / édition */}
      {isModalOpen && (
        <EmailTemplateModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTemplate(null);
          }}
          template={editingTemplate}
          onSubmit={handleSubmit}
        />
      )}

      {/* Confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) =>
          setDeleteConfirm({ open, template: open ? deleteConfirm.template : null })
        }
        title="Supprimer le modèle"
        description={
          deleteConfirm.template
            ? `Êtes-vous sûr de vouloir supprimer le modèle "${deleteConfirm.template.name}" ?`
            : ''
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </PageLayout>
  );
}
